import { merge } from 'lodash-es';
import * as Domains from '../domains';
import { DEV } from '../env/constants';
import * as Location from '../location';
import { ConfigUrl, Type } from '../types';
import * as Url from '../url';
import { defaultedOpt } from '../util';
import BaseAuthService, {
  BaseAuthHooks,
  BaseAuthNames,
  BaseAuthServiceOptions,
} from './BaseAuthService';
import { AuthService } from './types';

// these values are defaulted here, but their defaults may be overridden by
// the auth service construction process...to essentially a different default
// to the outside user, especially for common values like customerWebView and
// requireAuthentication
const DEFAULT_OPTIONS: Partial<ExternalAuthServiceOptions> = {
  authUrl: {
    path: 'v0/ciam-public/me',
    scheme: 'https',
  },
  hooks: {
    // hooks is declared to make BaseAuthService checks safer
  },
  loginUrl: {
    path: '/usis/login',
    scheme: 'https',
  },
  logoutUrl: {
    path: '/usis/login/logout',
    scheme: 'https',
  },
  names: {
    paramLoginReturnUrl: 'pdurl',
    paramLogoutGotoUrl: 'sdurl',
  },
};

// given an environment, returns the options specific to that environment
function defaultOptionsForEnv(
  location: Location,
  env: string,
  baseDomain: string,
): Partial<ExternalAuthServiceOptions> {
  const cloudApiDomain = Domains.External.cloudApiDomain(env, baseDomain);
  const cloudWebDomain = Domains.External.cloudWebDomain(env, baseDomain);

  const options: Partial<ExternalAuthServiceOptions> = {
    authUrl: {
      host: cloudApiDomain, // ex: api.dev.troweprice.com
    },
    hooks: {
      loginReturnUrlBuilder: () => location.href,
    },
    loginUrl: {
      host: cloudWebDomain, // ex: wwwdev.troweprice.com
    },
    logoutUrl: {
      host: cloudWebDomain, // ex: wwwdev.troweprice.com
    },
  };

  if (env === DEV) {
    options.echoUrl = { host: cloudApiDomain };
  }

  return options;
}

// Public
// ======

export type ExternalAuthHooks = BaseAuthHooks;

export interface ExternalAuthNames extends BaseAuthNames {
  // the name of the param to redirect to after logout
  paramLogoutGotoUrl: string;
}

export interface ExternalAuthServiceOptions extends BaseAuthServiceOptions {
  // a set of overridable/pluggable function-based behavior, see their type
  // definition for more details
  hooks?: ExternalAuthHooks;
  // the configured url for external login
  loginUrl?: ConfigUrl;
  // the configured url for external logout
  logoutUrl?: ConfigUrl;
  // names for various pieces of functionality (header names, param names), see
  // their type definition for more details
  names?: Partial<ExternalAuthNames>;
}

function withDefaults(options: ExternalAuthServiceOptions): ExternalAuthServiceOptions {
  const collapsedOptions = merge(
    {},
    DEFAULT_OPTIONS,
    defaultOptionsForEnv(options.location, options.environment, options.baseDomain),
    options,
  );
  // for whatever reason, in IE11 the location is changed in a way that makes it no longer
  // work by the above merge(), since the location isn't being defaulted, this reassigns
  // it so it will work again
  collapsedOptions.location = options.location;
  return collapsedOptions;
}

/**
 * An auth service that handles external users (TRP customers). See
 * verifyAuthentication() for a brief description of the external auth workflow.
 *
 * While this can be used directly, it is likely that the newAuthService()
 * constructor is desirable as it supports common and automatic confiugration.
 */
export default class ExternalAuthService extends BaseAuthService implements AuthService {
  private readonly loginUrl: ConfigUrl;
  private readonly logoutUrl: ConfigUrl;
  private readonly names: ExternalAuthNames;

  constructor(options: ExternalAuthServiceOptions) {
    options = withDefaults(options);
    super(options);
    this.loginUrl = defaultedOpt(options.loginUrl);
    this.logoutUrl = defaultedOpt(options.logoutUrl);
    this.names = defaultedOpt(options.names) as ExternalAuthNames;
  }

  /**
   * The login page accepts a redirect url to go to after login is successful.
   * The current login setup embeds another redirect url into that one, however.
   * The return url from this function, then, is a url with another url in
   * a query parameter.
   *
   * @param url
   * @param returnUrl
   */
  private withReturnUrl(url: string, returnUrl?: ConfigUrl | null) {
    returnUrl = this.coerceReturnUrl(returnUrl);
    // add return url to initial redirect url
    const loginUrl = Url.withQueryParams(url, {
      [this.names.paramLoginReturnUrl]: Location.buildOptionalUrl(
        this.location,
        returnUrl,
      ),
    });
    return Location.buildUrl(this.location, loginUrl);
  }

  // AuthService
  // ===========

  buildLoginUrl(returnUrl?: string | null) {
    const loginUrl = Location.buildUrl(this.location, this.loginUrl);
    return this.withReturnUrl(loginUrl, returnUrl);
  }

  logout(gotoUrl?: string | null) {
    let logoutUrl = this.logoutUrl;
    if (gotoUrl) {
      logoutUrl = Url.withQueryParams(logoutUrl, {
        [this.names.paramLogoutGotoUrl]: gotoUrl,
      });
    }
    const url = Location.buildUrl(this.location, logoutUrl);
    // this promise intentionally does not resolve or reject, it is expected that the redirect
    // will occur and the entire app will be unloaded
    return new Promise<void>(() => {
      window.location.assign(url);
    });
  }

  type(): Type {
    return 'external';
  }
}
